// Login / Register / 2FA page.
// Mirrors the legacy LoginPage in app/frontend/index.html line by line.

import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { useDict } from "@/i18n/LocaleProvider";
import { api, ApiError } from "@/lib/api";
import { useAuthStore } from "@/stores/authStore";
import {
  isEmailVerificationChallenge,
  isTwoFactorChallenge,
  type LoginResponse,
  type TokenResponse,
} from "@/types/auth";

type Mode = "login" | "register";

// Survives the email-verification gap between register and first login,
// so a "Get Pro" click on the landing page resumes checkout afterwards.
const POST_AUTH_NEXT_KEY = "postAuthNext";

// Open-redirect guard: only allow internal in-app destinations, never an
// absolute or protocol-relative URL.
function safeNext(raw: string | null): string | null {
  if (!raw || !raw.startsWith("/app")) return null;
  return raw;
}

interface ProvidersResponse {
  enabled: string[];
}

function formatError(err: unknown): string {
  if (err instanceof ApiError) {
    return typeof err.detail === "string" ? err.detail : JSON.stringify(err.detail);
  }
  if (err instanceof Error) return err.message;
  return String(err);
}

export function LoginPage() {
  const navigate = useNavigate();
  const t = useDict().app.auth;
  const setSession = useAuthStore((s) => s.setSession);
  const [searchParams] = useSearchParams();

  const [mode, setMode] = useState<Mode>(
    searchParams.get("mode") === "register" ? "register" : "login",
  );
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [orgName, setOrgName] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [providers, setProviders] = useState<string[]>([]);

  // 2FA state
  const [twofaChallenge, setTwofaChallenge] = useState<string | null>(null);
  const [twofaCode, setTwofaCode] = useState("");

  // Load configured OAuth providers on mount
  useEffect(() => {
    api<ProvidersResponse>("/auth/oauth/providers")
      .then((d) => setProviders(d.enabled ?? []))
      .catch(() => setProviders([]));
  }, []);

  // Stash a post-auth destination so it survives register → verify-email
  // → login before being consumed.
  useEffect(() => {
    const next = safeNext(searchParams.get("next"));
    if (next) sessionStorage.setItem(POST_AUTH_NEXT_KEY, next);
  }, [searchParams]);

  function consumeNext(): string {
    const n = sessionStorage.getItem(POST_AUTH_NEXT_KEY);
    if (n) sessionStorage.removeItem(POST_AUTH_NEXT_KEY);
    return safeNext(n) ?? "/app";
  }

  async function submit() {
    setBusy(true);
    setErr(null);
    try {
      const endpoint = mode === "login" ? "/auth/login" : "/auth/register";
      const body =
        mode === "login"
          ? { email, password }
          : {
              email,
              password,
              full_name: fullName,
              organization_name: orgName,
              consent_accepted: consent,
            };
      const data = await api<LoginResponse>(endpoint, {
        method: "POST",
        body,
      });
      if (isTwoFactorChallenge(data)) {
        setTwofaChallenge(data.challenge_token);
        setBusy(false);
        return;
      }
      if (isEmailVerificationChallenge(data)) {
        // Login refused — user is registered but hasn't verified their
        // email yet. Send them to the resend page; the email is carried
        // in route state so they don't have to retype it.
        navigate("/verify-email-required", {
          replace: true,
          state: { email: data.email },
        });
        return;
      }
      await setSession(data.access_token, data.refresh_token);
      // After registration, /auth/register returns tokens directly but
      // the user is still email_verified=false. Send them to the
      // required-page so they read "check your inbox" before reaching
      // /app (where any scan attempt would 403 anyway).
      if (mode === "register") {
        navigate("/verify-email-required", {
          replace: true,
          state: { email },
        });
        return;
      }
      navigate(consumeNext(), { replace: true });
    } catch (e) {
      setErr(formatError(e));
      setBusy(false);
    }
  }

  async function submit2FA() {
    setBusy(true);
    setErr(null);
    try {
      const data = await api<TokenResponse>("/auth/2fa/login", {
        method: "POST",
        body: { challenge_token: twofaChallenge, code: twofaCode },
      });
      await setSession(data.access_token, data.refresh_token);
      navigate(consumeNext(), { replace: true });
    } catch (e) {
      setErr(formatError(e));
      setBusy(false);
    }
  }

  function cancel2FA() {
    setTwofaChallenge(null);
    setTwofaCode("");
    setErr(null);
  }

  function oauthLogin(provider: string) {
    window.location.href = `/api/v1/auth/oauth/${provider}/login`;
  }

  const canSubmit =
    !busy &&
    !!email &&
    !!password &&
    (mode === "login" || (!!fullName && consent));

  // ─── 2FA challenge screen ────────────────────────────────────────────────
  if (twofaChallenge) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center p-5">
        <div className="w-full max-w-[420px]">
          <div className="text-center mb-8">
            <img
              src="/logo.png"
              alt="TrueHacking"
              className="h-20 w-auto mb-4 mx-auto"
            />
            <div className="font-display text-[32px] font-bold text-accent tracking-[0.14em]">
              TRUEHACKING
            </div>
            <div className="font-mono text-[10px] text-dim tracking-[0.1em] mt-1 uppercase">
              {t.twofaTitle}
            </div>
          </div>

          <Card className="!p-5 sm:!p-7">
            <div className="font-display text-[18px] font-semibold text-content mb-1">
              {t.twofaHeading}
            </div>
            <div className="font-mono text-[11px] text-muted mb-5">
              {t.twofaHint}
            </div>
            <div
              onKeyDown={(e) => {
                if (e.key === "Enter" && twofaCode && !busy) submit2FA();
              }}
            >
              <Input
                label={t.codeLabel}
                value={twofaCode}
                onChange={setTwofaCode}
                placeholder={t.codePlaceholder}
                className="mb-4"
              />
            </div>

            {err && (
              <div className="px-3 py-2 bg-danger-bg border border-danger/40 rounded text-danger font-mono text-[11px] mb-3">
                {err}
              </div>
            )}

            <Button
              variant="primary"
              full
              onClick={submit2FA}
              disabled={busy || !twofaCode}
            >
              {busy ? t.busy : t.verify}
            </Button>

            <div className="mt-3 text-center">
              <button
                onClick={cancel2FA}
                className="bg-transparent border-none text-muted font-mono text-[11px] cursor-pointer underline"
              >
                {t.backToSignin}
              </button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // ─── Login / Register screen ─────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-bg flex items-center justify-center p-5">
      <div className="w-full max-w-[420px]">
        {/* Branding */}
        <div className="text-center mb-8">
          <img
            src="/logo.png"
            alt="TrueHacking"
            className="h-20 w-auto mb-4 mx-auto"
          />
          <div className="font-display text-[32px] font-bold text-accent tracking-[0.14em]">
            TRUEHACKING
          </div>
          <div className="font-mono text-[10px] text-dim tracking-[0.1em] mt-1 uppercase">
            {t.copilot}
          </div>
          <div className="font-mono text-[10px] text-dim italic mt-[14px]">
            {t.tagline}
          </div>
        </div>

        <Card className="!p-5 sm:!p-7">
          <div className="font-display text-[18px] font-semibold text-content mb-1">
            {mode === "login" ? t.signIn : t.createAccount}
          </div>
          <div className="font-mono text-[11px] text-muted mb-5">
            {mode === "login" ? t.signInSub : t.registerSub}
          </div>

          {/* OAuth buttons */}
          {providers.length > 0 && (
            <div className="mb-[18px]">
              {providers.includes("google") && (
                <button
                  onClick={() => oauthLogin("google")}
                  className="w-full px-4 py-3 min-h-[44px] bg-white border border-[#dadce0] rounded text-[#3c4043] font-mono text-[13px] font-semibold cursor-pointer flex items-center justify-center gap-[10px] mb-2"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  {t.google}
                </button>
              )}
              {providers.includes("github") && (
                <button
                  onClick={() => oauthLogin("github")}
                  className="w-full px-4 py-3 min-h-[44px] bg-[#24292f] border border-[#1b1f24] rounded text-white font-mono text-[13px] font-semibold cursor-pointer flex items-center justify-center gap-[10px] mb-2"
                >
                  <svg width="18" height="18" viewBox="0 0 16 16" fill="#fff">
                    <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z" />
                  </svg>
                  {t.github}
                </button>
              )}
              <div className="flex items-center gap-[10px] mt-3 mb-1">
                <div className="flex-1 h-px bg-stroke" />
                <span className="font-mono text-[9px] text-dim uppercase tracking-[0.1em]">
                  {t.or}
                </span>
                <div className="flex-1 h-px bg-stroke" />
              </div>
            </div>
          )}

          <div
            onKeyDown={(e) => {
              if (e.key === "Enter" && canSubmit) submit();
            }}
          >
            <Input
              label={t.email}
              value={email}
              onChange={setEmail}
              placeholder={t.emailPlaceholder}
            />
            <Input
              label={t.password}
              value={password}
              onChange={setPassword}
              placeholder={t.passwordPlaceholder}
              type="password"
            />
            {mode === "register" && (
              <Input
                label={t.fullName}
                value={fullName}
                onChange={setFullName}
                placeholder={t.fullNamePlaceholder}
              />
            )}
            {mode === "register" && (
              <Input
                label={t.org}
                value={orgName}
                onChange={setOrgName}
                placeholder={t.orgPlaceholder}
              />
            )}

            {/* LGPD consent */}
            {mode === "register" && (
              <div className="mb-[14px] px-3 py-[10px] bg-bg2 border border-stroke rounded">
                <label className="flex items-start gap-[10px] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    className="mt-[3px] cursor-pointer accent-accent"
                  />
                  <span className="font-mono text-[11px] text-muted leading-[1.5]">
                    {t.consentPre}{" "}
                    <Link
                      to="/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-accent underline"
                    >
                      {t.terms}
                    </Link>{" "}
                    {t.consentAnd}{" "}
                    <Link
                      to="/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-accent underline"
                    >
                      {t.privacy}
                    </Link>
                    {t.consentPost}
                  </span>
                </label>
              </div>
            )}

            {err && (
              <div className="px-3 py-2 bg-danger-bg border border-danger/40 rounded text-danger font-mono text-[11px] mb-3">
                {err}
              </div>
            )}

            <Button
              variant="primary"
              full
              onClick={submit}
              disabled={!canSubmit}
            >
              {busy ? t.busy : mode === "login" ? t.signIn : t.createAccount}
            </Button>
          </div>

          <div className="mt-4 text-center flex flex-col gap-2">
            <button
              onClick={() => {
                setMode(mode === "login" ? "register" : "login");
                setErr(null);
              }}
              className="bg-transparent border-none text-accent font-mono text-[11px] cursor-pointer underline"
            >
              {mode === "login" ? t.needAccount : t.haveAccount}
            </button>
            {mode === "login" && (
              <button
                onClick={() => navigate("/forgot-password")}
                className="bg-transparent border-none text-muted font-mono text-[11px] cursor-pointer underline"
              >
                {t.forgot}
              </button>
            )}
          </div>

          {providers.length === 0 && (
            <div className="mt-5 pt-4 border-t border-stroke text-center font-mono text-[9px] text-dim">
              {t.noOauth}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
